import React, { useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ActivityIndicator, ScrollView } from 'react-native';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { createOrganization } from '../services/organizationService';

export default function AuthScreen({ showHome = false, onBackToHome }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [activeTab, setActiveTab] = useState('login'); // 'login' or 'register'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [nameFocused, setNameFocused] = useState(false);

  React.useEffect(() => {
    document.title = activeTab === 'login' ? 'Flomodo - Sign In' : 'Flomodo - Sign Up';
  }, [activeTab]);

  const handleAuth = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    if (activeTab === 'register') {
      if (!displayName) {
        setError('Please enter your name');
        return;
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    setLoading(true);
    setError('');

    try {
      if (activeTab === 'login') {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        // Register new user
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        // Create user profile
        await setDoc(doc(db, 'users', user.uid), {
          email: user.email,
          displayName: displayName,
          createdAt: serverTimestamp(),
          userType: 'user',
        });

        // Create personal workspace
        await createOrganization(user.uid, `${displayName}'s Workspace`);
      }
    } catch (err) {
      let errorMessage = err.message;
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email is already registered';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password';
      } else if (err.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ScrollView 
      style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}
      contentContainerStyle={styles.contentContainer}
    >
      {showHome && onBackToHome && (
        <TouchableOpacity 
          style={[styles.backButton, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]} 
          onPress={onBackToHome}
        >
          <Text style={[styles.backButtonText, { color: colors.accent }]}>← Back to Home</Text>
        </TouchableOpacity>
      )}
      
      <View style={[styles.authCard, { ...glass, backgroundColor: colors.card, borderColor: colors.border }]}>
        <View style={styles.logoContainer}>
          <Text style={[styles.logo, { color: colors.accent }]}>Flomodo</Text>
        </View>

        {/* Tab Navigation */}
        <View style={[styles.tabContainer, { backgroundColor: colors.secondary, borderColor: colors.border }]}>
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'login' && [styles.activeTab, { backgroundColor: colors.accent }]
            ]}
            onPress={() => {
              setActiveTab('login');
              setError('');
            }}
          >
            <Text style={[
              styles.tabText,
              { color: activeTab === 'login' ? '#fff' : colors.text }
            ]}>
              Sign In
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'register' && [styles.activeTab, { backgroundColor: colors.accent }]
            ]}
            onPress={() => {
              setActiveTab('register');
              setError('');
            }}
          >
            <Text style={[
              styles.tabText,
              { color: activeTab === 'register' ? '#fff' : colors.text }
            ]}>
              Sign Up
            </Text>
          </TouchableOpacity>
        </View>

        <Text style={[styles.title, { color: colors.text }]}>
          {activeTab === 'login' ? 'Welcome Back' : 'Create Account'}
        </Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
          {activeTab === 'login' 
            ? 'Sign in to continue your workflow' 
            : 'Start your productivity journey today'}
        </Text>

        {activeTab === 'register' && (
          <View style={styles.inputContainer}>
            <Text style={[styles.inputLabel, { color: colors.text }]}>Full Name</Text>
            <TextInput
              style={[
                styles.input,
                { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text },
                nameFocused && [styles.inputFocused, { borderColor: colors.accent }]
              ]}
              placeholder="John Doe"
              placeholderTextColor={colors.textTertiary}
              value={displayName}
              onChangeText={(text) => {
                setDisplayName(text);
                setError('');
              }}
              onFocus={() => setNameFocused(true)}
              onBlur={() => setNameFocused(false)}
            />
          </View>
        )}

        <View style={styles.inputContainer}>
          <Text style={[styles.inputLabel, { color: colors.text }]}>Email</Text>
          <TextInput
            style={[
              styles.input,
              { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text },
              emailFocused && [styles.inputFocused, { borderColor: colors.accent }]
            ]}
            placeholder="you@example.com"
            placeholderTextColor={colors.textTertiary}
            value={email}
            onChangeText={(text) => {
              setEmail(text);
              setError('');
            }}
            onFocus={() => setEmailFocused(true)}
            onBlur={() => setEmailFocused(false)}
            autoCapitalize="none"
            keyboardType="email-address"
          />
        </View>

        <View style={styles.inputContainer}>
          <Text style={[styles.inputLabel, { color: colors.text }]}>Password</Text>
          <View style={styles.passwordInputWrapper}>
            <TextInput
              style={[
                styles.input,
                styles.passwordInput,
                { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text },
                passwordFocused && [styles.inputFocused, { borderColor: colors.accent }]
              ]}
              placeholder={activeTab === 'login' ? 'Enter your password' : 'Create a strong password'}
              placeholderTextColor={colors.textTertiary}
              value={password}
              onChangeText={(text) => {
                setPassword(text);
                setError('');
              }}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              secureTextEntry={!showPassword}
            />
            <TouchableOpacity 
              style={styles.passwordToggle}
              onPress={() => setShowPassword(!showPassword)}
            >
              <Text style={styles.passwordToggleText}>{showPassword ? '👁️' : '👁️‍🗨️'}</Text>
            </TouchableOpacity>
          </View>
          {activeTab === 'register' && (
            <Text style={[styles.passwordHint, { color: colors.textTertiary }]}>
              Minimum 6 characters
            </Text>
          )}
        </View>

        {activeTab === 'register' && (
          <View style={styles.inputContainer}>
            <Text style={[styles.inputLabel, { color: colors.text }]}>Confirm Password</Text>
            <TextInput
              style={[
                styles.input,
                { backgroundColor: colors.secondary, borderColor: colors.border, color: colors.text }
              ]}
              placeholder="Re-enter your password"
              placeholderTextColor={colors.textTertiary}
              value={confirmPassword}
              onChangeText={(text) => {
                setConfirmPassword(text);
                setError('');
              }}
              secureTextEntry={!showPassword}
            />
          </View>
        )}

        {error ? (
          <Text style={[styles.error, { color: colors.error, backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)', borderColor: colors.error }]}>
            {error}
          </Text>
        ) : null}

        <TouchableOpacity 
          style={[styles.button, { backgroundColor: colors.accent }]} 
          onPress={handleAuth}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.buttonText}>
              {activeTab === 'login' ? 'Sign In' : 'Create Account'}
            </Text>
          )}
        </TouchableOpacity>

        {activeTab === 'login' && (
          <TouchableOpacity style={styles.forgotPassword}>
            <Text style={[styles.forgotPasswordText, { color: colors.textSecondary }]}>
              Forgot password?
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  contentContainer: {
    flexGrow: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    minHeight: '100vh',
  },
  backButton: {
    position: 'absolute',
    top: 30,
    left: 30,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    borderWidth: 1,
  },
  backButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
  authCard: {
    width: '100%',
    maxWidth: 480,
    borderRadius: 24,
    padding: 48,
    borderWidth: 1,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    fontSize: 42,
    fontWeight: '800',
    letterSpacing: -1,
  },
  tabContainer: {
    flexDirection: 'row',
    borderRadius: 12,
    padding: 4,
    marginBottom: 32,
    borderWidth: 1,
  },
  tab: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    transition: 'all 0.3s ease',
  },
  activeTab: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  tabText: {
    fontSize: 15,
    fontWeight: '700',
  },
  title: {
    fontSize: 32,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 15,
    marginBottom: 32,
    textAlign: 'center',
    lineHeight: 22,
  },
  inputContainer: {
    marginBottom: 20,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 8,
  },
  input: {
    borderRadius: 12,
    padding: 16,
    fontSize: 15,
    borderWidth: 1,
    outlineStyle: 'none',
    transition: 'all 0.2s ease',
  },
  inputFocused: {
    boxShadow: '0 0 0 3px rgba(74, 158, 255, 0.1)',
  },
  passwordInputWrapper: {
    position: 'relative',
  },
  passwordInput: {
    paddingRight: 50,
  },
  passwordToggle: {
    position: 'absolute',
    right: 12,
    top: '50%',
    transform: 'translateY(-50%)',
    padding: 8,
  },
  passwordToggleText: {
    fontSize: 20,
  },
  passwordHint: {
    fontSize: 12,
    marginTop: 6,
  },
  button: {
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    marginTop: 8,
    boxShadow: '0 4px 15px rgba(74, 158, 255, 0.3)',
  },
  buttonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '700',
  },
  forgotPassword: {
    marginTop: 20,
    alignItems: 'center',
  },
  forgotPasswordText: {
    fontSize: 14,
  },
  error: {
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 10,
    borderWidth: 1,
  },
});
